import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Link, Navigate, Route, Routes } from 'react-router-dom';
import { AdminUsersPage } from './features/admin-users/AdminUsersPage.js';
import { AuditPage } from './features/audit/AuditPage.js';
import { EvlIntakePage } from './features/evl/EvlIntakePage.js';
import { EvlRunStatusPage } from './features/evl/EvlRunStatusPage.js';
import { IntegrationDetailPage } from './features/integrations/IntegrationDetailPage.js';
import { IntegrationsListPage } from './features/integrations/IntegrationsListPage.js';
import { MembersPage } from './features/members/MembersPage.js';
import { ModuleDetailPage } from './features/modules/ModuleDetailPage.js';
import { ModulesListPage } from './features/modules/ModulesListPage.js';
import { useMe } from './features/me/use-me.js';
import { loginRequest } from './lib/msal.js';
import { ThemeToggle } from './lib/ThemeToggle.js';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, retry: false },
  },
});

export function App(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <UnauthenticatedTemplate>
        <LoginScreen />
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>
        <AppShell />
      </AuthenticatedTemplate>
    </QueryClientProvider>
  );
}

function LoginScreen(): JSX.Element {
  const { instance } = useMsal();
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 px-4">
      <div className="absolute right-4 top-4">
        <ThemeToggle />
      </div>
      <h1 className="text-3xl font-semibold text-slate-900 dark:text-slate-100">
        Ionic HR Automations
      </h1>
      <p className="max-w-md text-center text-slate-600 dark:text-slate-400">
        Sign in with your Ionic Entra account to continue.
      </p>
      <button
        type="button"
        onClick={() => {
          void instance.loginRedirect(loginRequest);
        }}
        className="rounded-md bg-brand-600 px-5 py-2 font-medium text-white shadow-sm hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500"
      >
        Sign in
      </button>
    </div>
  );
}

function AppShell(): JSX.Element {
  return (
    <div className="min-h-screen">
      <Header />
      <main className="mx-auto max-w-6xl px-6 py-8">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/companies/:companyId/integrations" element={<IntegrationsListPage />} />
          <Route
            path="/companies/:companyId/integrations/:provider/:name"
            element={<IntegrationDetailPage />}
          />
          <Route path="/companies/:companyId/members" element={<MembersPage />} />
          <Route path="/companies/:companyId/modules" element={<ModulesListPage />} />
          <Route path="/companies/:companyId/modules/:moduleId" element={<ModuleDetailPage />} />
          <Route path="/companies/:companyId/modules/evl/runs/new" element={<EvlIntakePage />} />
          <Route
            path="/companies/:companyId/modules/evl/runs/:runId"
            element={<EvlRunStatusPage />}
          />
          <Route path="/audit" element={<AuditPage />} />
          <Route path="/admin/users" element={<AdminUsersPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function Header(): JSX.Element {
  const { instance, accounts } = useMsal();
  const { me } = useMe();
  const account = accounts[0];
  const hasIonic = (me?.ionicRoles ?? []).length > 0;
  return (
    <header className="border-b border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
        <Link to="/" className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          Ionic HR Automations
        </Link>
        <div className="flex items-center gap-4 text-sm text-slate-600 dark:text-slate-300">
          {hasIonic && (
            <Link
              to="/admin/users"
              className="font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400"
            >
              Users (Admin)
            </Link>
          )}
          <span>{me?.displayName ?? account?.name ?? account?.username ?? 'Signed in'}</span>
          <ThemeToggle />
          <button
            type="button"
            className="rounded-md border border-slate-300 px-3 py-1 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"
            onClick={() => {
              void instance.logoutRedirect();
            }}
          >
            Sign out
          </button>
        </div>
      </div>
    </header>
  );
}

function HomePage(): JSX.Element {
  const { me, loading, error } = useMe();
  const hasIonic = useMemo(() => (me?.ionicRoles ?? []).length > 0, [me]);

  if (loading) return <p className="text-slate-500">Loading…</p>;
  if (error) {
    return (
      <div className="rounded-md border border-red-300 bg-red-50 p-4 text-red-800">
        <p className="font-medium">Could not load your profile.</p>
        <p className="text-sm">{error.message}</p>
      </div>
    );
  }
  if (!me) return <p className="text-slate-500">Not signed in.</p>;

  return (
    <div className="space-y-6">
      <section>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Welcome, {me.displayName}</h1>
            <p className="text-sm text-slate-600">{me.email}</p>
            {hasIonic && (
              <p className="mt-2 inline-flex items-center rounded-full bg-brand-100 px-3 py-0.5 text-xs font-medium text-brand-700">
                Ionic role: {me.ionicRoles.join(', ')}
              </p>
            )}
          </div>
          {(hasIonic || me.companies.some((c) => c.roles.includes('company_admin'))) && (
            <Link to="/audit" className="text-sm font-medium text-brand-600 hover:text-brand-700">
              Audit log →
            </Link>
          )}
        </div>
      </section>

      <section>
        <h2 className="mb-3 text-lg font-semibold">Your companies</h2>
        {me.companies.length === 0 ? (
          <p className="text-slate-500">
            You don&rsquo;t have any company assignments yet. Ask an Ionic admin to grant access.
          </p>
        ) : (
          <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white">
            {me.companies.map((c) => {
              const canManageMembers = hasIonic || c.roles.includes('company_admin');
              return (
                <li key={c.companyId} className="flex items-center justify-between px-4 py-3">
                  <div>
                    <p className="font-medium text-slate-900">{c.companyName}</p>
                    <p className="text-xs text-slate-500">{c.roles.join(', ')}</p>
                  </div>
                  <div className="flex items-center gap-4 text-sm font-medium">
                    <Link
                      to={`/companies/${c.companyId}/modules`}
                      className="text-brand-600 hover:text-brand-700"
                    >
                      Modules →
                    </Link>
                    <Link
                      to={`/companies/${c.companyId}/integrations`}
                      className="text-brand-600 hover:text-brand-700"
                    >
                      Integrations →
                    </Link>
                    {canManageMembers && (
                      <Link
                        to={`/companies/${c.companyId}/members`}
                        className="text-brand-600 hover:text-brand-700"
                      >
                        Members →
                      </Link>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
